import React, { useContext, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import moment from "moment";
import { DataGrid } from "@mui/x-data-grid";

import ThemeContext from "../lib/theme-context";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import columnsAll from "../lib/columnsReflections";

import useWindowDimensions from "../utils/width";

const getReflectionQuestions = (timeframe, reviewType) => {
  if (reviewType === "Weekly") {
    return {
      question_1: `What went well this week? What specific trades or decisions am I proud of?`,
      question_2: `What didn't go well this week? What mistakes did I make and what can I learn from them?`,
      question_3: `How was my emotional discipline this week? Did I follow my rules or let emotions take over?`,
      question_4: `Did I miss any trades this week? What were the reasons and how can I be better prepared next week?`,
      question_5: `What is the #1 thing I need to focus on improving next week?`,
    };
  }

  if (reviewType === "Monthly") {
    return {
      question_1: `What did I accomplish this month? (How did I move the needle?)`,
      question_2: `What are the negatives/cons about the trades I took this month?`,
      question_3: `What are the positives/pros about the trades I took this month?`,
      question_4: `How do I bridge the gap between those weak points and strong points?`,
      question_5: `How do I feel about my work ethic? Do my actions align with my aspirations and goals?`,
      question_6: `How were my emotions when I was in trades? How were my emotions when I missed trades?`,
      question_7: `How do I feel about myself this month?`,
      question_8: `Things I will do more of:`,
      question_9: `Things I will do less of:`,
      question_10: `Anything else I want to add (other areas of my life, things I am grateful for, etc.)`,
      question_11: `Common reason: What can I do to protect against these in the future?`,
      question_12: `2nd Common reason: What can I do to protect against these in the future?`,
      question_13: `3rd Common reason: What can I do to protect against these in the future?`,
    };
  }

  if (reviewType === "Quarterly" || reviewType === "Q1" || reviewType === "Q2" || reviewType === "Q3" || reviewType === "Q4") {
    return {
      question_1: `What are your greatest technical strengths?`,
      question_2: `What are your worst technical weaknesses?`,
      question_3: `What are your greatest opportunities to grow in your technicals?`,
      question_4: `What do you think are the greatest threats to your technicals? (i.e., the technical traps you usually fall into that cause you to give back the most profit)`,
      question_5: `What are your greatest psychological strengths?`,
      question_6: `What are your worst psychological weaknesses?`,
      question_7: `What are your greatest opportunities to grow in your mindset?`,
      question_8: `What do you think are the greatest threats to your trading psychology? (i.e., the mindset or emotional traps you usually fall into that cause you to give back the most profit)`,
      question_9: `90-Day Plan Evaluation: Look back at your 90-Day Plan from this quarter. Did you achieve everything you set out to achieve? Detail the reasons why you fell short, matched, or exceeded your expectations.`,
      question_10: `Work Ethic & Alignment: How do I feel about my work ethic? Do my actions truly align with my aspirations and goals?`,
      question_11: `Defining Success: What do I absolutely have to achieve this year in order for me to consider it a success?`,
      question_12: `Things I will do more of:`,
      question_13: `Things I will do less of:`,
      question_14: `Anything else I want to add (other areas of my life, things I am grateful for, etc.)`,
    };
  }

  if (reviewType === "Annual") {
    return {};
  }

  return {
    question_1: `Did I execute 100% according to my trading plan this ${timeframe}? (What did I do well, and where did I deviate?)`,
    question_2: `Based on this ${timeframe}'s data, what is one specific habit I need to double down on, and one specific mistake I must eliminate next ${timeframe}?`,
    question_3: `Rate my emotional discipline on a scale of 1-10 when managing active trades and after missed trades during this ${timeframe}. Did I chase the market, or did I accept the missed opportunity?`,
    question_4: `Honestly evaluate this ${timeframe}'s work ethic: Did my daily preparation, routine, and discipline match the standard of the successful trader I intend to become?`,
    question_5: `Outside of the charts, what external factors affected my trading this ${timeframe} (sleep, health, environment)? What am I genuinely grateful for?`,
  };
};

const SharedReflection = () => {
  const { width } = useWindowDimensions();
  const theme = useContext(ThemeContext);

  const [reflection, setReflection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSharedReflection = async () => {
      try {
        const pathParts = window.location.pathname.split('/');
        const typeOfReview = pathParts[2];
        const dateSlug = pathParts[3];
        
        const apiUrl = `${process.env.REACT_APP_API}/shared-reflection/${typeOfReview}/${dateSlug}`;
        
        const response = await fetch(apiUrl);
        
        if (response.ok) {
          const data = await response.json();
          setReflection(data);
        } else {
          const errorMessage = await response.text();
          if (response.status === 404) {
            setError("Reflection not found");
          } else {
            setError(errorMessage || "Error fetching reflection");
          }
        }
      } catch (err) {
        console.error("Error fetching shared reflection:", err);
        setError("Error fetching reflection");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedReflection();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        }}
      >
        <div style={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }}>
          Loading reflection...
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        }}
      >
        <div style={{ 
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          textAlign: "center",
          padding: "40px",
          maxWidth: "500px"
        }}>
          <ErrorOutlineIcon 
            sx={{ 
              fontSize: "80px", 
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              marginBottom: "20px"
            }} 
          />
          <h2 style={{ 
            fontSize: "24px", 
            fontWeight: "600",
            marginBottom: "12px",
            color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e"
          }}>
            Reflection Not Found
          </h2>
          <p style={{ 
            fontSize: "16px",
            lineHeight: "1.6",
            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280"
          }}>
            {error}
          </p>
        </div>
      </Box>
    );
  }

  if (!reflection) {
    return null;
  }

  const questions = getReflectionQuestions(reflection.typeOfReview, reflection.typeOfReview);
  const questionKeys = Object.keys(questions);

  
  return (
    <Box
      sx={{
        overflowY: "scroll",
        overflowX: "hidden",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
        width: "100%",
        maxWidth: "1920px",
        height: "100%",
        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        padding: "0px 15px 15px 15px",
        margin: "0 auto",
      }}
    >
      <Grid
        container
        justifyContent="space-evenly"
        alignItems="center"
        sx={{ minHeight: "100%" }}
      >
        <Grid
          item
          xs={12}
          sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
        >
          <div
            style={{
              maxWidth: "95%",
              margin: width <= 768 ? "0.5em auto" : "0.5em auto",
              padding: width <= 768 ? "16px 20px" : "20px 32px",
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                padding: "16px",
                margin: "16px 0",
                borderRadius: "12px",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(45, 46, 51, 0.4)"
                    : "rgba(249, 250, 251, 0.8)",
                border: `1px solid ${
                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                }`,
                boxShadow:
                  theme[0] === "dark"
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {reflection.typeOfReview && (
                  <span
                    style={{
                      fontSize: "26px",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                    }}
                  >
                    {reflection.typeOfReview}
                  </span>
                )}

                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    letterSpacing: "0.3px",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.7)"
                        : "rgba(26, 27, 30, 0.7)",
                  }}
                >
                  {reflection.date && moment(reflection.date).format("DD/MM/YY")}
                </span>

                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    letterSpacing: "0.3px",
                    color:
                      theme[0] === "dark"
                        ? "rgba(252, 252, 255, 0.5)"
                        : "rgba(26, 27, 30, 0.5)",
                  }}
                >
                  Reflection
                </span>
              </div>
            </div>

            {/* Trades DataGrid */}
            {reflection.trades && reflection.trades.length > 0 && (
              <div style={{ marginTop: "32px" }}>
                <DataGrid
                  sx={{
                    width: "100%",
                    height: "500px",
                    border: `1px solid ${theme[0] === "dark" ? "#4a4b50" : "#d1d5db"}`,
                    "& .MuiSvgIcon-root": {
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: theme[0] === "dark" ? "#25262b" : "#f9fafb",
                      borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      color: theme[0] === "dark" ? "#c9d1d9" : "#374151",
                      fontWeight: "600",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      overflowX: "auto",
                      "&::-webkit-scrollbar": {
                        height: "12px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor:
                          theme[0] === "dark" ? "rgba(60, 62, 66)" : "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor:
                          theme[0] === "dark" ? "rgba(120, 120, 120)" : "#888",
                        borderRadius: "6px",
                        "&:hover": {
                          backgroundColor:
                            theme[0] === "dark"
                              ? "rgba(140, 140, 140)"
                              : "#555",
                        },
                      },
                    },
                    backgroundColor:
                      theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                    color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    "& .MuiDataGrid-cell": {
                      cursor: "pointer",
                      borderBottom: "none",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    },
                    "& .MuiTablePagination-actions": {
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: theme[0] === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                    },
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-row": {
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                    },
                  }}
                  rows={reflection.trades}
                  columns={columnsAll(
                    theme,
                    null, // throwMessage - not available in public view
                    null, // idTradeGenerator - not needed for reflections
                    null, // runDurationCal - not needed for reflections
                    null, // handleOpenJournal - not needed for reflections
                    null, // auth - not available in public view
                    null, // calculateFields - not needed for reflections
                    reflection.sharedAccountId || null, // Use the accountId stored at time of sharing
                    null, // setSingleJournalView
                    null, // setSingleReflectionView
                    null, // setOpenJournal
                    null, // setSingleForecastView
                    null, // handleCloseNewReflection
                    null, // reflectionTrades
                    reflection.sharedAccountType || null // Use the accountType stored at time of sharing
                  ).filter(col => {
                    // Filter out journalID and foreCastId for public view
                    if (col.field === 'journalID' || col.field === 'foreCastId') return false;
                    return true;
                  })}
                  autoPageSize
                  rowHeight={53}
                />
              </div>
            )}

            <div style={{ marginTop: "24px" }}>
              {questionKeys.map((key, index) => {
                const questionNumber = index + 1;
                const answerKey = `question_${questionNumber}`;
                const answer = reflection[answerKey];

                if (!answer) return null;

                return (
                  <React.Fragment key={`fragment-${questionNumber}`}>
                    {/* Add section headers based on reflection type */}
                    {reflection.typeOfReview === "Monthly" && questionNumber === 1 && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          marginTop: "2em",
                          marginBottom: "1em",
                          textAlign: "center",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 1: General Reflections
                      </div>
                    )}
                    
                    {reflection.typeOfReview?.includes("Q") && questionNumber === 1 && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          marginTop: "2em",
                          marginBottom: "1em",
                          textAlign: "center",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 1: Technical SWOT Analysis
                        <div style={{
                          fontSize: "14px",
                          fontWeight: "400",
                          marginTop: "0.5em",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        }}>
                          (SWOT = Strengths, Weaknesses, Opportunities, Threats)
                        </div>
                      </div>
                    )}
                    
                    {reflection.typeOfReview === "Monthly" && questionNumber === 11 && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          marginTop: "1em",
                          marginBottom: "1em",
                          textAlign: "center",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 2: How to Miss Less Trades Next Month
                      </div>
                    )}
                    
                    {reflection.typeOfReview?.includes("Q") && questionNumber === 5 && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          marginTop: "2em",
                          marginBottom: "1em",
                          textAlign: "center",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 2: Psychological SWOT Analysis
                        <div style={{
                          fontSize: "14px",
                          fontWeight: "400",
                          marginTop: "0.5em",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        }}>
                          (SWOT = Strengths, Weaknesses, Opportunities, Threats)
                        </div>
                      </div>
                    )}
                    
                    {reflection.typeOfReview?.includes("Q") && questionNumber === 9 && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          marginTop: "1em",
                          marginBottom: "1em",
                          textAlign: "center",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        Part 3: Deep Dive Performance Review
                      </div>
                    )}

                    <div
                      style={{
                        margin: "16px 0",
                        padding: "20px",
                        borderRadius: "12px",
                        backgroundColor:
                          theme[0] === "dark"
                            ? "rgba(45, 46, 51, 0.4)"
                            : "rgba(249, 250, 251, 0.8)",
                        border: `1px solid ${
                          theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          letterSpacing: "0.5px",
                          color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                          margin: "0 0 12px 0",
                        }}
                      >
                        QUESTION {questionNumber}
                      </p>
                      <p
                        style={{
                          fontSize: "17px",
                          fontWeight: "600",
                          fontStyle: "italic",
                          margin: "0 0 4px 0",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {questions[key]}
                      </p>
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.6",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          margin: 0,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {answer}
                      </p>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SharedReflection;
